//Proofed
import { useState } from 'react'
import { Box, Container, Grid, Typography, Stack, Chip, Card, CardContent, Accordion, AccordionSummary, AccordionDetails } from '@mui/material'
import { BookDemoModal } from '../../components/BookDemoModal'
import { FancyGoldButton } from '../../components/FancyGoldButton'
import { FancyLightButton } from '../../components/FancyLightButton'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { COLOR_TERTIARY, BID_HERO_START, BID_HERO_MID, BID_HERO_END, CHECKCIRCLE_SIZE, SECTION_DARK, SECTION_LIGHT, BG_LIGHT, DARK_800 } from '../../theme'
import { TrustedPartnerSection } from '../../components/TrustedPartnerSection'
import { HowItWorksSection } from '../../components/HowItWorksSection'
import PageMeta from '../../components/PageMeta'
import { CtaSection } from '../../components/CtaSection'
import {
    bidHighlights,
    bidSteps,
    bidTriggers,
    bidServices,
    bidWhoFor,
    bidFaqs,
    bidHIWTitle,
    bidHIWSubtitle,
} from '../../common/ProductBidServicesContent'
import { steps as approachSteps } from '../../common/NetworkProvenApproachContent'
import { WaveDivider } from '../../components/WaveDivider'
// import { bidTestimonials } from '../../common/Testimonials'

export default function BidServices() {
    const [demoOpen, setDemoOpen] = useState(false)

    return (
        <Box>
            <PageMeta
                title="Bid Services"
                description="Expert bid writing support, bid reviews, market research and public sector onboarding from the Contract Finder Pro advisory team. Fixed-price proposals and fast turnaround."
                canonical="/products/bid-services"
            />
            {/* Hero */}
            <Box sx={{
                background: `linear-gradient(135deg, ${BID_HERO_START} 0%, ${BID_HERO_MID} 50%, ${BID_HERO_END} 100%)`,
                ...SECTION_DARK,
                pt: { xs: 4, md: 6 },
            }}>
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                display: 'inline-flex', alignItems: 'center', gap: 1.5,
                                backgroundColor: `${COLOR_TERTIARY}12`,
                                border: `1px solid ${COLOR_TERTIARY}70`,
                                borderRadius: 2, px: 2, py: 1, mb: 3,
                            }}>
                                <Box sx={{
                                    width: 32, height: 32, borderRadius: 1,
                                    backgroundColor: `${COLOR_TERTIARY}20`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <SupportAgentIcon sx={{ color: COLOR_TERTIARY, fontSize: 22 }} />
                                </Box>
                                <Typography variant="caption" sx={{ color: COLOR_TERTIARY, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                    Advisory Services
                                </Typography>
                            </Box>

                            <Typography variant="h1" sx={{ color: 'white', fontSize: { xs: '36px', md: '52px' }, mb: 2 }}>
                                CFP <Box component="span" sx={{ color: COLOR_TERTIARY }}>Bid Services</Box>
                            </Typography>
                            <Typography variant="h4" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 400, mb: 3, lineHeight: 1.5 }}>
                                Expert Bid Support, When You Need It
                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '17px', mb: 4, lineHeight: 1.7 }}>
                                Delievered by sales professionals with decades of UK Government experience, CFP Bid Services puts expert human support alongside our platform for the bids that matter most, the submissions that need a second opinion and teams taking their first steps into public sector work
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <FancyGoldButton color={COLOR_TERTIARY} endIcon={<ArrowForwardIcon />} onClick={() => setDemoOpen(true)}>
                                    Book a Discovery Call
                                </FancyGoldButton>
                                <FancyLightButton to="/contactus" color={COLOR_TERTIARY}>
                                    Talk to the Team
                                </FancyLightButton>
                            </Stack>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                background: 'rgba(255,255,255,0.04)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: 3,
                                p: 3,
                            }}>
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600 }}>
                                    What we offer
                                </Typography>
                                <Stack spacing={1.5} sx={{ mt: 2 }}>
                                    {bidHighlights.map(h => (
                                        <Box key={h} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                            <CheckCircleIcon sx={{ color: COLOR_TERTIARY, fontSize: CHECKCIRCLE_SIZE, mt: 0.15, flexShrink: 0 }} />
                                            <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '14px' }}>{h}</Typography>
                                        </Box>
                                    ))}
                                </Stack>
                                {/* <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>
                                        Pricing is case-by-case. Fixed-price proposal before any work begins.
                                    </Typography>
                                </Box> */}
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />
            {/* Services grid */}
            <Box sx={{ ...SECTION_LIGHT }}>
                <Container maxWidth="lg">
                    <Typography variant="h2" sx={{ textAlign: 'center', mb: 2 }}>Our Services</Typography>
                    <Typography color="text.secondary" sx={{ textAlign: 'center', mb: 8, maxWidth: 520, mx: 'auto' }}>
                        Four ways we support UK & Ireland public sector suppliers from first bid to major framework submission.
                    </Typography>
                    <Grid container spacing={4}>
                        {bidServices.map(service => (
                            <Grid item xs={12} sm={6} key={service.title}>
                                <Card sx={{ height: '100%' }}>
                                    <CardContent sx={{ p: 4 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                                            <Box sx={{
                                                width: 48, height: 48, borderRadius: 2,
                                                backgroundColor: `${COLOR_TERTIARY}15`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                flexShrink: 0,
                                            }}>
                                                <service.Icon sx={{ color: COLOR_TERTIARY, fontSize: 36 }} />
                                            </Box>
                                            {service.tag && (
                                                <Chip
                                                    label={service.tag}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: `${COLOR_TERTIARY}15`,
                                                        color: COLOR_TERTIARY,
                                                        border: `1px solid ${COLOR_TERTIARY}35`,
                                                        fontWeight: 600,
                                                        fontSize: '11px',
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        <Typography variant="h5" sx={{ mb: 1.5 }}>{service.title}</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>{service.body}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />
            {/* When to use us trigger bar */}
            <Box sx={{ ...SECTION_DARK }}>
                <Container maxWidth="lg">
                    <Typography variant="h4" sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', fontWeight: 400, mb: 6, fontStyle: 'italic', fontSize: { xs: '16px', md: '18px' } }}>
                        "The best bids are won before a word is written with the right expertise behind you."
                    </Typography>
                    <Grid container spacing={3} justifyContent="center">
                        {bidTriggers.map((item, i) => (
                            <Grid item xs={12} md={4} key={i}>
                                <Box sx={{
                                    background: 'rgba(255,255,255,0.04)',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    borderRadius: 2,
                                    p: 3,
                                    height: '100%',
                                }}>
                                    <Typography sx={{ color: COLOR_TERTIARY, fontWeight: 700, fontSize: '15px', mb: 1 }}>
                                        {item.heading}
                                    </Typography>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '14px', lineHeight: 1.7 }}>
                                        {item.body}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />



            {/* ── The five-step approach ── */}
            <Box sx={{ backgroundColor: 'white', py: { xs: 6, md: 10 } }}>
                <Container maxWidth="lg">
                    <Typography variant="h2" sx={{ textAlign: 'center', mb: 2 }}>
                        The approach behind the service
                    </Typography>
                    <Typography color="text.secondary" sx={{ textAlign: 'center', mb: { xs: 6, md: 10 }, maxWidth: 580, mx: 'auto' }}>
                        Our bid services are built around a proven five-step methodology. Understanding where you sit in this journey helps us focus our support where it will have the greatest impact.
                    </Typography>
                    <Stack spacing={6}>
                        {approachSteps.map((step, i) => (
                            <Box key={step.number} sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: i % 2 === 0 ? 'row' : 'row-reverse' },
                                gap: { xs: 4, md: 6 },
                                alignItems: 'flex-start',
                            }}>
                                {/* Icon + step number */}
                                <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{
                                        width: 72, height: 72, borderRadius: 3,
                                        background: `linear-gradient(135deg, ${BID_HERO_START} 0%, ${BID_HERO_MID} 100%)`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0,
                                    }}>
                                        <step.Icon sx={{ color: COLOR_TERTIARY, fontSize: 32 }} />
                                    </Box>
                                    <Typography sx={{ color: 'rgba(0,0,0,0.35)', fontWeight: 800, fontSize: '13px', letterSpacing: '2px' }}>
                                        STEP {step.number}
                                    </Typography>
                                </Box>

                                {/* Content */}
                                <Box sx={{ flex: 1 }}>
                                    <Chip label={step.label} size="small" sx={{
                                        mb: 1.5, backgroundColor: `${COLOR_TERTIARY}15`, color: COLOR_TERTIARY,
                                        border: `1px solid ${COLOR_TERTIARY}50`, fontWeight: 700, fontSize: '11px',
                                    }} />
                                    <Typography variant="h3" sx={{ fontSize: { xs: '22px', md: '26px' }, mb: 2, lineHeight: 1.3 }}>
                                        {step.heading}
                                    </Typography>
                                    <Typography color="text.secondary" sx={{ fontSize: '15px', lineHeight: 1.8, mb: 3, maxWidth: 560 }}>
                                        {step.body}
                                    </Typography>
                                    <Grid container spacing={1}>
                                        {step.bullets.map(b => (
                                            <Grid item xs={12} sm={6} key={b}>
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                    <CheckCircleIcon sx={{ color: COLOR_TERTIARY, fontSize: 16, mt: 0.3, flexShrink: 0 }} />
                                                    <Typography sx={{ fontSize: '14px', color: 'text.secondary', lineHeight: 1.55 }}>{b}</Typography>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                    <Box sx={{ textAlign: 'center', mt: 8 }}>
                        <FancyLightButton to="/success-network/proven-approach" endIcon={<ArrowForwardIcon />} color={COLOR_TERTIARY}>
                            Read the full five-step guide
                        </FancyLightButton>
                    </Box>
                </Container>
            </Box>
            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />
            <HowItWorksSection
                title={bidHIWTitle}
                subtitle={bidHIWSubtitle}
                steps={bidSteps}
                dark={true}
            />
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />

            {/* Who this is for */}
            <Box sx={{ ...SECTION_LIGHT }}>
                <Container maxWidth="md">
                    <Box sx={{
                        background: `linear-gradient(135deg, ${COLOR_TERTIARY}10, ${COLOR_TERTIARY}05)`,
                        border: `1px solid ${COLOR_TERTIARY}25`,
                        borderRadius: 3,
                        p: { xs: 4, md: 6 },
                    }}>
                        <Typography variant="h4" sx={{ mb: 1 }}>Who uses CFP Bid Services?</Typography>
                        <Typography color="text.secondary" sx={{ mb: 4, fontSize: '15px' }}>
                            Our services are best suited to teams that already understand public sector procurement, or are ready to start. We're not a "list of leads" we're expert support for businesses serious about winning.
                        </Typography>
                        <Grid container spacing={3}>
                            {bidWhoFor.map(item => (
                                <Grid item xs={12} sm={6} key={item.role}>
                                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                        <CheckCircleIcon sx={{ color: COLOR_TERTIARY, fontSize: CHECKCIRCLE_SIZE, mt: 0.2, flexShrink: 0 }} />
                                        <Box>
                                            <Typography variant="body2" fontWeight={700}>{item.role}</Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{item.desc}</Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Container>
            </Box>

            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />
            {/* FAQ */}
            <Box sx={{ ...SECTION_DARK }}>
                <Container maxWidth="md">
                    <Typography variant="h2" sx={{ textAlign: 'center', mb: 2, color: 'white' }}>Common questions</Typography>
                    <Typography sx={{ textAlign: 'center', mb: 6, color: 'rgba(255,255,255,0.55)' }}>
                        Everything you need to know before getting in touch.
                    </Typography>
                    <Stack spacing={1.5}>
                        {bidFaqs.map(faq => (
                            <Accordion key={faq.q} disableGutters elevation={0} sx={{
                                border: '1px solid rgba(0,0,0,0.08)',
                                borderRadius: '10px !important',
                                '&:before': { display: 'none' },
                                '&.Mui-expanded': { border: `1px solid ${COLOR_TERTIARY}40` },
                            }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3, py: 0.5 }}>
                                    <Typography sx={{ fontWeight: 600, fontSize: '15px' }}>{faq.q}</Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 3, pb: 2.5 }}>
                                    <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>{faq.a}</Typography>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Stack>
                </Container>
            </Box>
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />
            <TrustedPartnerSection />

            <CtaSection
                title="Need expert support on your next bid?"
                subtitle="Book a no-obligation discovery call"
                onDemoClick={() => setDemoOpen(true)}
                showJoinButton={false}
                demobutton='Book a Discovery Call'
            />

            <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
        </Box>
    )
}
